import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api';

interface LoginProps {
  onLogin: (token: string, user: {name: string, email: string, role: string, modules: string[]}) => void;
}

export default function Login({ onLogin }: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState<'credentials'|'code'|'reset_request'|'reset_new'>('credentials');
  const [userId, setUserId] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset_token');
    if (token) {
      setResetToken(token);
      setStep('reset_new');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleLogin = async () => {
    if (!email || !password) { setError('Wpisz email i hasło'); return; }
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/auth/request-code`, { email, password });
      setUserId(res.data.user_id);
      setMaskedPhone(res.data.masked_phone);
      setStep('code');
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Błąd logowania');
    }
    setLoading(false);
  };

  const handleVerify = async () => {
    if (!code || code.length !== 6) { setError('Wpisz 6-cyfrowy kod'); return; }
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/auth/verify-code`, { user_id: userId, code });
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify({
        name: res.data.name, email, role: res.data.role, modules: res.data.modules || []
      }));
      onLogin(res.data.access_token, { name: res.data.name, email, role: res.data.role, modules: res.data.modules || [] });
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Nieprawidłowy kod');
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setError(''); setCode('');
    try {
      const res = await axios.post(`${API}/auth/request-code`, { email, password });
      setUserId(res.data.user_id);
      setMaskedPhone(res.data.masked_phone);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Błąd wysyłania kodu');
    }
  };

  const handleResetRequest = async () => {
    if (!email) { setError('Wpisz adres email'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/auth/reset-password-request`, { email });
      setSuccessMsg('Jeśli konto istnieje, wysłaliśmy link resetujący na podany adres email.');
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Błąd wysyłania');
    }
    setLoading(false);
  };

  const handleResetNew = async () => {
    if (!newPassword || !confirmPassword) { setError('Wypełnij oba pola'); return; }
    if (newPassword !== confirmPassword) { setError('Hasła nie są identyczne'); return; }
    if (newPassword.length < 8) { setError('Hasło musi mieć minimum 8 znaków'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: newPassword });
      setSuccessMsg('Hasło zostało zmienione. Możesz się teraz zalogować.');
      setTimeout(() => { setStep('credentials'); setSuccessMsg(''); setNewPassword(''); setConfirmPassword(''); }, 3000);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Błąd zmiany hasła');
    }
    setLoading(false);
  };

  const inp = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' as const, outline: 'none' };

  const stepTitle = {
    credentials: 'Zaloguj się do systemu',
    code: 'Weryfikacja SMS',
    reset_request: 'Reset hasła',
    reset_new: 'Nowe hasło',
  }[step];

  return (
    <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center',
      background: '#f9fafb', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
        padding: 40, width: 360, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✉️</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: '#111827' }}>OPENkontakt</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{stepTitle}</div>
        </div>

        {error && (
          <div style={{ padding: '10px 14px', background: '#fef2f2', color: '#dc2626',
            borderRadius: 6, fontSize: 13, marginBottom: 16, border: '1px solid #fca5a5' }}>
            {error}
          </div>
        )}

        {successMsg && (
          <div style={{ padding: '10px 14px', background: '#f0fdf4', color: '#15803d',
            borderRadius: 6, fontSize: 13, marginBottom: 16, border: '1px solid #bbf7d0' }}>
            {successMsg}
          </div>
        )}

        {step === 'credentials' && (
          <>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 5 }}>Email</label>
              <input value={email} onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                placeholder="twoj@email.pl" type="email" style={inp} />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 5 }}>Hasło</label>
              <div style={{ position: 'relative' }}>
                <input value={password} onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleLogin()}
                  placeholder="••••••••" type={showPassword ? 'text' : 'password'}
                  style={{ ...inp, padding: '9px 36px 9px 12px' }} />
                <button onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#9ca3af' }}>
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>
            <div style={{ textAlign: 'right', marginBottom: 20 }}>
              <button onClick={() => { setStep('reset_request'); setError(''); setSuccessMsg(''); }}
                style={{ background: 'none', border: 'none', color: '#1d4ed8', fontSize: 12, cursor: 'pointer', padding: 0 }}>
                Zapomniałem hasła
              </button>
            </div>
            <button onClick={handleLogin} disabled={loading}
              style={{ width: '100%', padding: '10px', background: loading ? '#9ca3af' : '#1d4ed8',
                color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500,
                cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Sprawdzam...' : 'Dalej →'}
            </button>
          </>
        )}

        {step === 'code' && (
          <>
            <div style={{ padding: '12px 16px', background: '#f0f9ff', border: '1px solid #bae6fd',
              borderRadius: 8, fontSize: 13, color: '#0369a1', marginBottom: 20, textAlign: 'center' }}>
              📱 Wysłaliśmy kod na numer <strong>{maskedPhone}</strong>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 5 }}>Kod SMS (6 cyfr)</label>
              <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                onKeyDown={e => e.key === 'Enter' && handleVerify()}
                placeholder="000000" type="text" maxLength={6} autoFocus
                style={{ ...inp, fontSize: 22, letterSpacing: 8, textAlign: 'center', fontWeight: 700 }} />
            </div>
            <button onClick={handleVerify} disabled={loading || code.length !== 6}
              style={{ width: '100%', padding: '10px', marginBottom: 10,
                background: loading || code.length !== 6 ? '#9ca3af' : '#1d4ed8',
                color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500,
                cursor: loading || code.length !== 6 ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Weryfikuję...' : 'Zaloguj się'}
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { setStep('credentials'); setCode(''); setError(''); }}
                style={{ flex: 1, padding: '8px', background: 'transparent', border: '1px solid #d1d5db',
                  borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#6b7280' }}>
                ← Wróć
              </button>
              <button onClick={handleResend}
                style={{ flex: 1, padding: '8px', background: 'transparent', border: '1px solid #d1d5db',
                  borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#6b7280' }}>
                Wyślij ponownie
              </button>
            </div>
          </>
        )}

        {step === 'reset_request' && (
          <>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
              Wpisz adres email powiązany z kontem. Wyślemy link do zresetowania hasła.
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 5 }}>Email</label>
              <input value={email} onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleResetRequest()}
                placeholder="twoj@email.pl" type="email" style={inp} />
            </div>
            {!successMsg && (
              <button onClick={handleResetRequest} disabled={loading}
                style={{ width: '100%', padding: '10px', marginBottom: 10,
                  background: loading ? '#9ca3af' : '#1d4ed8',
                  color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500,
                  cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Wysyłam...' : 'Wyślij link resetujący'}
              </button>
            )}
            <button onClick={() => { setStep('credentials'); setError(''); setSuccessMsg(''); }}
              style={{ width: '100%', padding: '8px', background: 'transparent', border: '1px solid #d1d5db',
                borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#6b7280' }}>
              ← Wróć do logowania
            </button>
          </>
        )}

        {step === 'reset_new' && (
          <>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
              Wpisz nowe hasło dla swojego konta.
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 5 }}>Nowe hasło (min. 8 znaków)</label>
              <input value={newPassword} onChange={e => setNewPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleResetNew()}
                placeholder="••••••••" type="password" style={inp} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 5 }}>Potwierdź hasło</label>
              <input value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleResetNew()}
                placeholder="••••••••" type="password" style={inp} />
            </div>
            {!successMsg && (
              <button onClick={handleResetNew} disabled={loading}
                style={{ width: '100%', padding: '10px',
                  background: loading ? '#9ca3af' : '#1d4ed8',
                  color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500,
                  cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Zmieniam...' : 'Ustaw nowe hasło'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
